import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Search, Filter, Star, MapPin } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import OptimizedImage from '../components/OptimizedImage';
import { searchService } from '../lib/database';
import type { College } from '../lib/supabase';
import toast from 'react-hot-toast';

const SearchResults = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  
  const [searchQuery, setSearchQuery] = useState(query);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedState, setSelectedState] = useState('all');
  const [sortBy, setSortBy] = useState('relevance');
  const [searchResults, setSearchResults] = useState<College[]>([]);
  const [loading, setLoading] = useState(true);

  const categories = ['all', 'Engineering', 'Medical', 'Arts & Sciences', 'Management'];
  const states = [
    'all',
    'Andhra Pradesh',
    'Arunachal Pradesh', 
    'Assam',
    'Bihar',
    'Chhattisgarh',
    'Goa',
    'Gujarat',
    'Haryana',
    'Himachal Pradesh',
    'Jharkhand',
    'Karnataka',
    'Kerala',
    'Madhya Pradesh',
    'Maharashtra',
    'Manipur',
    'Meghalaya',
    'Mizoram',
    'Nagaland',
    'Odisha',
    'Punjab',
    'Rajasthan',
    'Sikkim',
    'Tamil Nadu',
    'Telangana',
    'Tripura',
    'Uttar Pradesh',
    'Uttarakhand',
    'West Bengal',
    'Andaman and Nicobar Islands',
    'Chandigarh',
    'Dadra and Nagar Haveli and Daman and Diu',
    'Delhi',
    'Jammu and Kashmir',
    'Ladakh',
    'Lakshadweep',
    'Puducherry'
  ];
  const sortOptions = [
    { value: 'relevance', label: 'Relevance' },
    { value: 'rating', label: 'Rating' },
    { value: 'fees-low', label: 'Fees: Low to High' },
    { value: 'fees-high', label: 'Fees: High to Low' }
  ];

  useEffect(() => {
    if (query) {
      performSearch();
    }
  }, [query, selectedCategory, selectedState]);

  const performSearch = async () => {
    try {
      setLoading(true);
      const results = await searchService.searchColleges(query, {
        category: selectedCategory,
        state: selectedState
      });
      setSearchResults(results);
    } catch (error) {
      console.error('Error searching colleges:', error);
      toast.error('Failed to search colleges');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (searchQuery.trim()) {
      setSearchParams({ q: searchQuery.trim() });
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  if (loading) {
    return (
      <div style={{ paddingTop: '80px' }} className="bg-light">
        <div className="container py-5">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Searching colleges...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: '80px' }} className="bg-light">
      <SEOHead
        title={`Search Results for "${query}" | Grad Vikalp`}
        description={`Find colleges matching "${query}". Browse engineering, medical, and other colleges based on your search criteria. Compare fees, ratings, and facilities.`}
        keywords={`${query}, college search, find colleges, ${selectedCategory !== 'all' ? selectedCategory + ' colleges, ' : ''}${selectedState !== 'all' ? selectedState + ' colleges, ' : ''}college comparison`}
        url={`/search?q=${encodeURIComponent(query)}`}
        type="website"
      />
      <div className="container py-5">
        {/* Search Header */}
        <div className="mb-4">
          <h1 className="h2 fw-bold text-dark mb-2">
            Search Results {query && `for "${query}"`}
          </h1>
          <p className="text-muted">
            Found {searchResults.length} college{searchResults.length !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Search Bar */}
        <div className="card border-0 shadow-lg mb-4">
          <div className="card-body p-4">
            <div className="d-flex align-items-center gap-3">
              <div className="flex-grow-1 position-relative">
                <Search className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" size={20} />
                <input
                  type="text"
                  placeholder="Search colleges..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="form-control form-control-lg ps-5"
                />
              </div>
              <button 
                onClick={handleSearch}
                className="btn btn-primary btn-lg"
              >
                Search
              </button>
            </div>
          </div>
        </div>

        <div className="row g-4">
          {/* Filters Sidebar */}
          <div className="col-lg-3">
            <div className="card border-0 shadow-lg sticky-top" style={{ top: '100px' }}>
              <div className="card-body p-4">
                <h3 className="h5 fw-bold text-dark mb-4 d-flex align-items-center gap-2">
                  <Filter size={20} />
                  Filters
                </h3>

                <div className="vstack gap-4">
                  {/* Category Filter */}
                  <div>
                    <label className="form-label fw-semibold text-dark">
                      Category
                    </label>
                    <select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      className="form-select"
                    >
                      {categories.map(category => (
                        <option key={category} value={category}>
                          {category === 'all' ? 'All Categories' : category}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* State Filter */}
                  <div>
                    <label className="form-label fw-semibold text-dark">
                      State
                    </label>
                    <select
                      value={selectedState}
                      onChange={(e) => setSelectedState(e.target.value)}
                      className="form-select"
                    >
                      {states.map(state => (
                        <option key={state} value={state}>
                          {state === 'all' ? 'All States' : state}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Clear Filters */}
                  <button 
                    onClick={() => {
                      setSelectedCategory('all');
                      setSelectedState('all');
                    }}
                    className="btn btn-outline-secondary w-100"
                  >
                    Clear All Filters
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="col-lg-9">
            {/* Sort Options */}
            <div className="d-flex justify-content-between align-items-center mb-4">
              <p className="text-muted mb-0">
                Showing {searchResults.length} results
              </p>
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted small">Sort by:</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="form-select form-select-sm"
                  style={{ width: 'auto' }}
                >
                  {sortOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Results List */}
            <div className="vstack gap-4">
              {searchResults.map((college) => (
                <Link
                  key={college.id}
                  to={`/college/${college.slug}`}
                  className="card border-0 shadow-lg text-decoration-none college-card"
                >
                  <div className="row g-0">
                    <div className="col-md-4">
                      <OptimizedImage
                        src={college.image_url}
                        alt={`${college.name} - ${college.category} college in ${college.location}, ${college.state}`}
                        title={`${college.name} | ${college.type} ${college.category} College | Rating: ${college.rating}/5`}
                        className="img-fluid h-100 w-100"
                        style={{ objectFit: 'cover', minHeight: '200px' }}
                        loading="lazy"
                      />
                    </div>
                    
                    <div className="col-md-8">
                      <div className="card-body p-4">
                        <div className="d-flex justify-content-between align-items-start mb-3">
                          <div>
                            <h3 className="h4 fw-bold text-dark mb-1">{college.name}</h3>
                            <div className="d-flex align-items-center gap-3 text-muted small">
                              <div className="d-flex align-items-center gap-1">
                                <MapPin size={14} />
                                <span>{college.location}</span>
                              </div>
                              <span>•</span>
                              <span>{college.category}</span>
                            </div>
                          </div>
                          
                          <div className="d-flex align-items-center gap-1">
                            <Star className="star-rating" size={16} fill="currentColor" />
                            <span className="fw-semibold">{college.rating}</span>
                          </div>
                        </div>

                        <p className="text-muted mb-4 line-clamp-2">
                          {college.description}
                        </p>

                        <div className="d-flex justify-content-between align-items-center">
                          <div className="d-flex gap-4 small">
                            <div>
                              <span className="text-muted">Fees:</span>
                              <span className="fw-semibold text-dark ms-1">{college.fees}</span>
                            </div>
                            <div>
                              <span className="text-muted">Cutoff:</span>
                              <span className="fw-semibold text-dark ms-1">{college.cutoff}</span>
                            </div>
                          </div>
                          
                          <span className="text-primary fw-semibold">
                            View Details →
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {searchResults.length === 0 && (
              <div className="text-center py-5">
                <h3 className="h4 text-muted mb-3">No colleges found</h3>
                <p className="text-muted mb-4">
                  Try adjusting your search terms or filters to find more results.
                </p>
                <Link to="/college-reviews" className="btn btn-primary">
                  Browse All Colleges
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchResults;